import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import { useFetch } from '@/hooks/useFetch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import ApperIcon from '@/components/ApperIcon';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { myOrders } from '@/shared/account';
import { committed, idOf } from '@/shared/data';
import { PLAN_VALUES, planName, planSummary } from '@/shared/pricing';
import { cn } from '@/lib/utils';

const PLAN_LABELS = Object.fromEntries(PLAN_VALUES.map((p) => [p, planName(p)]));

function shortName(user) {
  const first = user?.firstName || '';
  const last = user?.lastName ? ` ${user.lastName[0]}.` : '';
  return `${first}${last}`.trim();
}

export function RatingPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          onClick={() => onChange(n)}
          className="rounded-md p-1.5 transition-colors hover:bg-muted"
        >
          <ApperIcon
            name="Star"
            size={22}
            className={cn(n <= value ? 'fill-primary text-primary' : 'text-muted-foreground/40')}
          />
        </button>
      ))}
    </div>
  );
}

/** Write a review of one of your orders, or edit one you wrote. */
export default function ReviewDialog({ open, onOpenChange, review, defaultOrder, onSaved }) {
  const { user } = useAuth();
  const editing = !!review;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const orders = useFetch(
    () => (open ? myOrders(['Name', 'plan_c', 'term_c', 'connections_c', 'status_c']) : Promise.resolve([])),
    [open],
  );
  const orderList = orders.data ?? [];
  const orderById = Object.fromEntries(orderList.map((o) => [String(o.Id), o]));

  useEffect(() => {
    if (!open) return;
    if (review) {
      const order = idOf(review.order_c);
      setForm({
        Name: review.Name ?? '',
        order_c: order ? String(order) : '',
        display_name_c: review.display_name_c ?? '',
        plan_c: review.plan_c ?? '',
        rating_c: Number(review.rating_c || 5),
        body_c: review.body_c ?? '',
      });
    } else {
      setForm({
        Name: '',
        order_c: defaultOrder ? String(defaultOrder) : '',
        display_name_c: shortName(user),
        plan_c: '',
        rating_c: 5,
        body_c: '',
      });
    }
  }, [open]);

  // Fill the plan from the chosen order once the orders arrive.
  useEffect(() => {
    const o = orderById[form.order_c];
    if (o && !form.plan_c) setForm((f) => ({ ...f, plan_c: o.plan_c }));
  }, [form.order_c, orders.data]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  async function submit() {
    if (!form.Name?.trim()) {
      toast.error('Give your review a headline');
      return;
    }
    if (!form.order_c) {
      toast.error('Pick the order you are reviewing');
      return;
    }
    if (!form.body_c?.trim()) {
      toast.error('Say a few words about it');
      return;
    }
    const fields = {
      Name: form.Name.trim(),
      order_c: orderById[form.order_c]?.Id ?? idOf(review?.order_c) ?? form.order_c,
      display_name_c: form.display_name_c.trim() || 'Anonymous viewer',
      plan_c: form.plan_c || orderById[form.order_c]?.plan_c || 'Demon',
      rating_c: Number(form.rating_c || 5),
      body_c: form.body_c.trim(),
    };

    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('reviews_c').update({ Id: review.Id, ...fields })
        : await sdk.table('reviews_c').create({ ...fields, hidden_c: false });
      const saved = committed(res, editing ? 'Review updated' : 'Thanks! Your review is live.');
      if (!saved) return;
      onOpenChange(false);
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit your review' : 'Write a review'}
      description={editing ? undefined : 'Reviews show on the storefront with the name you choose.'}
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Post review'}
      busyLabel={editing ? 'Saving…' : 'Posting…'}
    >
      <Field label="Rating">
        <RatingPicker value={form.rating_c} onChange={set('rating_c')} />
      </Field>
      <Field label="Headline" htmlFor="r-name">
        <Input
          id="r-name"
          value={form.Name ?? ''}
          onChange={(e) => set('Name')(e.target.value)}
          placeholder="Set up in five minutes, zero buffering"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Order" htmlFor="r-order">
          <PickSelect
            id="r-order"
            value={form.order_c}
            onChange={(v) => setForm((f) => ({ ...f, order_c: v, plan_c: orderById[v]?.plan_c ?? f.plan_c }))}
            values={orderList.map((o) => String(o.Id))}
            labels={Object.fromEntries(orderList.map((o) => [String(o.Id), `${o.Name} · ${planSummary(o)}`]))}
            placeholder={orders.loading ? 'Loading your orders…' : 'Choose an order'}
          />
        </Field>
        <Field label="Plan" htmlFor="r-plan">
          <PickSelect
            id="r-plan"
            value={form.plan_c}
            onChange={set('plan_c')}
            values={PLAN_VALUES}
            labels={PLAN_LABELS}
            placeholder="Which plan"
          />
        </Field>
      </div>
      <Field label="Shown as" htmlFor="r-display" hint="First name and initial works well.">
        <Input
          id="r-display"
          value={form.display_name_c ?? ''}
          onChange={(e) => set('display_name_c')(e.target.value)}
          placeholder="Maya O."
        />
      </Field>
      <Field label="Your review" htmlFor="r-body">
        <Textarea
          id="r-body"
          rows={5}
          value={form.body_c ?? ''}
          onChange={(e) => set('body_c')(e.target.value)}
          placeholder="What you watch, on which devices, and how it has held up…"
        />
      </Field>
    </FormDialog>
  );
}
